'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    calculatePayroll,
    createPayrollRun,
    deletePayrollRun,
    getPayrollRuns,
    HrPayrollRun,
} from '@/lib/api/hr/hr-payroll-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdCalculate,
    MdClose,
    MdDelete,
    MdFilterList,
    MdPayments,
    MdSearch,
    MdUploadFile,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import PayrollImportModal from './payroll-import-modal'

const STATUS_BADGES: Record<string, string> = {
    DRAFT: 'badge-ghost',
    CALCULATING: 'badge-info',
    CALCULATED: 'badge-warning',
    APPROVED: 'badge-success',
    PAID: 'badge-secondary',
    CANCELLED: 'badge-error',
}

const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Rascunho',
    CALCULATING: 'Calculando',
    CALCULATED: 'Calculada',
    APPROVED: 'Aprovada',
    PAID: 'Paga',
    CANCELLED: 'Cancelada',
}

const TYPE_LABELS: Record<string, string> = {
    MONTHLY: 'Mensal',
    ADVANCE: 'Adiantamento',
    THIRTEENTH_1ST: '13o 1a Parcela',
    THIRTEENTH_2ND: '13o 2a Parcela',
    VACATION: 'Férias',
    TERMINATION: 'Rescisão',
    COMPLEMENTARY: 'Complementar',
}

const MONTH_OPTIONS = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
]

const currentYear = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function PayrollRunsListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('hr', 'payroll')

    const [runs, setRuns] = useState<HrPayrollRun[]>([])
    const [loading, setLoading] = useState(true)
    const [filterMonth, setFilterMonth] = useState('')
    const [filterYear, setFilterYear] = useState('')
    const [filterStatus, setFilterStatus] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Create modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [createForm, setCreateForm] = useState({
        month: String(new Date().getMonth()).padStart(2, '0'),
        year: String(currentYear),
        type: 'MONTHLY',
        notes: '',
    })
    const [saving, setSaving] = useState(false)

    // Import modal (holerites da contabilidade)
    const [importOpen, setImportOpen] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Calculate
    const [calculating, setCalculating] = useState<number | null>(null)

    useEffect(() => {
        loadRuns()
    }, [filterMonth, filterYear, filterStatus])

    const loadRuns = async () => {
        setLoading(true)
        try {
            const params: any = {}
            if (filterYear && filterMonth) params.competence = `${filterYear}-${filterMonth}`
            else if (filterYear) params.competence = filterYear
            if (filterStatus) params.status = filterStatus
            const res = await getPayrollRuns(params)
            if (res.success && res.result) {
                setRuns(res.result || [])
            } else {
                toast.error(res.error || 'Erro ao carregar folhas de pagamento')
            }
        } catch {
            toast.error('Erro ao carregar folhas de pagamento')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setCreateForm({
            month: String(new Date().getMonth()).padStart(2, '0'),
            year: String(currentYear),
            type: 'MONTHLY',
            notes: '',
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
    }

    const handleCreate = async () => {
        if (!createForm.month || !createForm.year) {
            toast.warning('Informe a competência')
            return
        }
        setSaving(true)
        try {
            const res = await createPayrollRun({
                competence: `${createForm.year}-${createForm.month}`,
                type: createForm.type,
                notes: createForm.notes || undefined,
            })
            if (res.success) {
                toast.success('Folha de pagamento criada com sucesso')
                closeModal()
                loadRuns()
            } else {
                toast.error(res.error || 'Erro ao criar folha de pagamento')
            }
        } catch {
            toast.error('Erro ao criar folha de pagamento')
        } finally {
            setSaving(false)
        }
    }

    const openImportModal = () => setImportOpen(true)

    const handleCalculate = async (id: number) => {
        setCalculating(id)
        try {
            const res = await calculatePayroll(id)
            if (res.success) {
                toast.success('Folha calculada com sucesso')
                loadRuns()
            } else {
                toast.error(res.error || 'Erro ao calcular folha')
            }
        } catch {
            toast.error('Erro ao calcular folha')
        } finally {
            setCalculating(null)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deletePayrollRun(deleteId)
            if (res.success) {
                toast.success('Folha excluída com sucesso')
                setDeleteId(null)
                loadRuns()
            } else {
                toast.error(res.error || 'Erro ao excluir folha')
            }
        } catch {
            toast.error('Erro ao excluir folha')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando folhas de pagamento...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPayments className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Folha de Pagamento
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {runs.length} {runs.length === 1 ? 'folha' : 'folhas'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button className="btn btn-outline gap-2" onClick={openImportModal}>
                            <MdUploadFile className="text-xl" />
                            <span className="hidden sm:inline">Importar Holerites</span>
                        </button>
                        <button className="btn btn-secondary gap-2" onClick={openCreateModal}>
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Nova Folha</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 flex items-center gap-2">
                        <MdSearch className="text-base-content/70 text-xl shrink-0" />
                        <select
                            className="select select-bordered flex-1"
                            value={filterMonth}
                            onChange={e => setFilterMonth(e.target.value)}
                        >
                            <option value="">Todos os meses</option>
                            {MONTH_OPTIONS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        <select
                            className="select select-bordered w-28"
                            value={filterYear}
                            onChange={e => setFilterYear(e.target.value)}
                        >
                            <option value="">Ano</option>
                            {YEAR_OPTIONS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    {(filterMonth || filterYear) && (
                        <button
                            type="button"
                            onClick={() => { setFilterMonth(''); setFilterYear('') }}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-outline gap-2"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Status</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            {runs.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdPayments className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma folha encontrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie uma nova folha de pagamento para começar
                        </p>
                        {canCreate && (
                            <button onClick={openCreateModal} className="btn btn-secondary gap-2">
                                <MdAdd className="text-xl" />
                                Nova Folha
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {runs.map(run => (
                        <div
                            key={run.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                            onClick={() => navigate(`/hr/payroll/${run.id}`)}
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-secondary/20 to-secondary/10 border-secondary/20 rounded-xl flex items-center justify-center border">
                                    <MdPayments className="text-3xl text-secondary" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div>
                                            <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                {TYPE_LABELS[run.type] || run.type} - {run.competence}
                                            </h3>
                                            <p className="text-sm text-base-content/60">
                                                {/* A lista é da organização: sem a filial,
                                                    duas folhas da mesma competência ficariam
                                                    indistinguíveis. */}
                                                {run.company?.name && (
                                                    <span className="font-medium text-base-content/80">
                                                        {run.company.name}
                                                        {' · '}
                                                    </span>
                                                )}
                                                {run._count?.items || 0} {(run._count?.items || 0) === 1 ? 'funcionário' : 'funcionários'}
                                                {run.notes && ` - ${run.notes}`}
                                            </p>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                            <button
                                                onClick={() => navigate(`/hr/payroll/${run.id}`)}
                                                className="btn btn-circle btn-sm btn-ghost"
                                                title="Ver detalhes"
                                            >
                                                <MdVisibility className="text-lg" />
                                            </button>
                                            {canUpdate && run.status === 'DRAFT' && (
                                                <button
                                                    onClick={() => handleCalculate(run.id)}
                                                    className="btn btn-circle btn-sm btn-ghost text-info"
                                                    title="Calcular"
                                                    disabled={calculating === run.id}
                                                >
                                                    {calculating === run.id ? (
                                                        <span className="loading loading-spinner loading-xs"></span>
                                                    ) : (
                                                        <MdCalculate className="text-lg" />
                                                    )}
                                                </button>
                                            )}
                                            {canDelete && run.status === 'DRAFT' && (
                                                <button
                                                    onClick={() => setDeleteId(run.id)}
                                                    className="btn btn-circle btn-sm btn-ghost text-error"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`badge ${STATUS_BADGES[run.status] || 'badge-ghost'}`}>
                                                {STATUS_LABELS[run.status] || run.status}
                                            </span>
                                            {run.origin === 'IMPORTED' && (
                                                <span className="badge badge-outline badge-sm gap-1">
                                                    <MdUploadFile className="text-sm" />
                                                    Importada
                                                </span>
                                            )}
                                            <span className="text-sm text-success font-medium">
                                                +{formatCurrency(run.totalEarnings)}
                                            </span>
                                            <span className="text-sm text-error font-medium">
                                                -{formatCurrency(run.totalDeductions)}
                                            </span>
                                            <span className="text-sm font-bold text-base-content">
                                                Liq: {formatCurrency(run.totalNet)}
                                            </span>
                                            {run.totalFgts > 0 && (
                                                <span className="badge badge-outline badge-sm">
                                                    FGTS: {formatCurrency(run.totalFgts)}
                                                </span>
                                            )}
                                        </div>

                                        <MdArrowForward className="text-xl text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">Nova Folha de Pagamento</h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Competência *</span>
                            </label>
                            <div className="flex gap-2">
                                <select
                                    className="select select-bordered flex-1"
                                    value={createForm.month}
                                    onChange={e => setCreateForm(prev => ({ ...prev, month: e.target.value }))}
                                >
                                    {MONTH_OPTIONS.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="select select-bordered w-28"
                                    value={createForm.year}
                                    onChange={e => setCreateForm(prev => ({ ...prev, year: e.target.value }))}
                                >
                                    {YEAR_OPTIONS.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={createForm.type}
                                onChange={e => setCreateForm(prev => ({ ...prev, type: e.target.value }))}
                            >
                                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Observações</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                rows={3}
                                placeholder="Observações sobre a folha..."
                                value={createForm.notes}
                                onChange={e => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleCreate} disabled={saving}>
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            Criar
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Import Modal */}
            <PayrollImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={loadRuns}
            />

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Folha de Pagamento"
                message="Tem certeza de que deseja excluir esta folha? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
